'use client';
import type { Review, Team } from '@baseball-near-you/prisma';
import {
  Button,
  Form,
  FormControl,
  FormInput,
  type FormProps,
  FormTextarea,
  LargeCheckboxInput,
  toast,
} from '@baseball-near-you/ui';
import { useUserProfile } from '@bb/lib/hooks/useUserProfile';
import { useGradeTeamMutation } from '@bb/lib/mutations/useGradeTeamMutation';
import { useReviewTeamMutation } from '@bb/lib/mutations/useReviewTeamMutation';
import { useTagReviewMutation } from '@bb/lib/mutations/useTagReviewMutation';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { ReviewStep, useReviewContext } from './AddReviewModal';

export function CreateReviewForm({
  onSubmit,
}: {
  onSubmit: (team: Team) => Promise<void>;
}) {
  const { userProfile } = useUserProfile();
  const { setStep, team, grades, selectedTags } = useReviewContext();
  const { reviewTeam, isReviewingTeam, reviewTeamError } =
    useReviewTeamMutation(team?.id!, { hideToast: true });
  const { gradeTeam, isGradingTeam, gradeTeamError } = useGradeTeamMutation(
    team?.id!
  );
  const { tagReview, isTaggingReview, tagReviewError } = useTagReviewMutation(
    team?.id!
  );

  const formProps: FormProps<Partial<Review>> = {
    onSubmit: async (data) => {
      const review = await reviewTeam({
        ...data,
        createdAt: new Date(),
        teamId: team?.id!,
        userId: userProfile?.id!,
      });

      await Promise.all([
        gradeTeam({ ...grades, teamId: team?.id!, reviewId: review?.id }),
        tagReview({ tags: selectedTags, reviewId: review?.id }),
      ]);
      toast.success('You have successfully submitted your review!');
      await onSubmit(team);
      setStep(ReviewStep.Message);
    },
    schema: z.object({
      title: z.string().min(1, 'Title is required'),
      body: z.string().min(1, 'Body is required'),
      wouldRecommend: z.boolean(),
    }),
    initialValues: {
      title: '',
      body: '',
      wouldRecommend: false,
      teamId: team?.id,
      userId: userProfile?.id,
    },
  };
  return (
    <Form {...formProps} className="space-y-5 rounded-md bg-white text-left">
      <FormBody />
      <Button
        loading={isReviewingTeam || isTaggingReview || isGradingTeam}
        type="submit"
        className="w-full"
      >
        Submit review
      </Button>
    </Form>
  );
}

export const FormBody = () => {
  const { watch, setValue } = useFormContext();
  const wouldRecommend = watch('wouldRecommend');

  return (
    <>
      <FormInput name="title" label="Title" />
      <FormTextarea name="body" label="Body" maxLength={1000} />
      <FormControl
        name="wouldRecommend"
        label="Would you recommend this program?"
      >
        <div className="flex gap-x-2">
          <LargeCheckboxInput
            label="Yes, I would."
            onChange={(e) => {
              setValue('wouldRecommend', true);
            }}
            checked={wouldRecommend}
          />
          <LargeCheckboxInput
            label="No, I would not."
            onChange={(e) => {
              setValue('wouldRecommend', false);
            }}
            checked={!wouldRecommend}
          />
        </div>
      </FormControl>
    </>
  );
};
